import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CertificationBadges = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [hoveredBadge, setHoveredBadge] = useState(null);

  const certifications = [
    {
      id: 1,
      name: "Workato Platform",
      issuer: "WORKATO | ACADEMY",
      category: "Automation",
      level: "Advanced",
      date: "2025-12-12",
      expiry: "2026-12-09",
      verified: true,
      icon: "Workflow",
      color: "#ff9900",
      skills: ["Create Collections using Workato API Platform", "Secure and control access to APIs (Policies, Clients & Access Profiles)", "Monitoring Features (Dashboard & Logging)"],
      description: "Understand what is an API and how does it fit in your organization. Create APIs using Workato API Platform.",
      verificationUrl: "http://verify.skilljar.com/c/y3m8fnbqcz98"
    },
    {
      id: 2,
      name: "Automation Pro 1 Exam",
      issuer: "WORKATO | ACADEMY",
      category: "Automation",
      level: "Professional",
      date: "2025-01-12",
      expiry: "2027-01-12",
      verified: true,
      icon: "Workflow",
      color: "#61dafb",
      skills: ["Recipe Building", "Trigger Conditions", "Data Mapping", "Formulas", "Recipe jobs"],
      description: "This Certificate validates that I passed a test proving a degree of expertise in Automation using Workato.",
      verificationUrl: "http://verify.skilljar.com/c/gtqq3zrkjrom"
    },
    {
      id: 3,
      name: "Automation Pro 2 Exam",
      issuer: "WORKATO | ACADEMY",
      category: "Automation",
      level: "Professional",
      date: "2025-01-21",
      expiry: "2027-01-21",
      verified: true,
      icon: "Workflow",
      color: "#4285f4",
      skills: ["Conditional Actions", "Repeat Actions", "Variables", "Lookup Tables"],
      description: "This Certificate validates that I passed a test proving a degree of expertise in advanced Automation using Workato.",
      verificationUrl: "http://verify.skilljar.com/c/29fr9p5gfc9x"
    },
    {
      id: 4,
      name: "Artificial Intelligence Fundamentals",
      issuer: "IBM SkillsBuild",
      category: "ai",
      level: "Associate",
      date: "2025-07-04",
      expiry: "2027-07-04",
      verified: true,
      icon: "Brain",
      color: "#326ce5",
      skills: ["AI Applications", "AI Capabilities", "AI Ethics","Artificial Neural Networks", "Machine Learning", "Chatbots", "Computer Vision"],
      description: "demonstrates knowledge of artificial intelligence (AI) concepts, such as natural language processing, computer vision, machine learning, deep learning, chatbots, and neural networks; AI ethics; and the applications of AI.",
      verificationUrl: "https://www.credly.com/badges/b164f992-1e05-49d1-80cb-587905449c22"
    },
    {
      id: 5,
      name: "Introduction to Critical Infrastructure Protection (ICIP)",
      issuer: "OPSWAT",
      category: "security",
      level: "Foundation",
      date: "2025-08-18",
      expiry: "2027-08-18",
      verified: true,
      icon: "Shield",
      color: "#47a248",
      skills: ["Critical Infrastructure", "Cybersecurity", "Threat Intelligence", "Incident Response", "Compliance"],
      description: "This course provides an overview of the critical infrastructure protection (CIP) framework, including the key components of the framework, the role of the CIP framework in protecting critical infrastructure, and the importance of CIP in the context of cybersecurity.",
      verificationUrl: "https://www.credly.com/badges/https://www.credly.com/badges/3bd41515-3bfd-426e-9fdd-5a1faf390282/public_url"
    },
    {
      id: 6,
      name: "Figma Design Systems Expert",
      issuer: "Figma",
      category: "design",
      level: "Expert",
      date: "2024-05-12",
      expiry: "2026-05-12",
      verified: true,
      icon: "Figma",
      color: "#f24e1e",
      skills: ["Design Systems", "Component Libraries", "Prototyping", "Collaboration"],
      description: "Expert-level certification in creating and maintaining design systems using Figma.",
      verificationUrl: "https://figma.com/certification/verify/FIGMA456"
    },
    {
      id: 7,
      name: "Cybersecurity Fundamentals",
      issuer: "CompTIA",
      category: "security",
      level: "Foundation",
      date: "2023-12-08",
      expiry: "2026-12-08",
      verified: true,
      icon: "Shield",
      color: "#e74c3c",
      skills: ["Network Security", "Risk Assessment", "Incident Response", "Compliance"],
      description: "Foundation-level cybersecurity certification covering essential security concepts and practices.",
      verificationUrl: "https://comptia.org/certifications/verify/SEC123"
    },
    {
      id: 8,
      name: "Machine Learning Engineer",
      issuer: "Google",
      category: "ai",
      level: "Professional",
      date: "2024-06-20",
      expiry: "2026-06-20",
      verified: false,
      icon: "Brain",
      color: "#ff6b35",
      skills: ["TensorFlow", "ML Pipelines", "Model Deployment", "Data Processing"],
      description: "Professional certification in machine learning engineering and model deployment on Google Cloud.",
      verificationUrl: "https://cloud.google.com/certification/verify/ML789"
    }
  ];

  const categories = [
    { value: 'all', label: 'All Certifications', icon: 'Award', count: certifications.length },
    { value: 'Automation', label: 'Automation', icon: 'Automation', count: certifications.filter(c => c.category === 'Automation').length },
    { value: 'frontend', label: 'Frontend Development', icon: 'Monitor', count: certifications.filter(c => c.category === 'frontend').length },
    { value: 'devops', label: 'DevOps & Infrastructure', icon: 'Settings', count: certifications.filter(c => c.category === 'devops').length },
    { value: 'database', label: 'Database Systems', icon: 'Database', count: certifications.filter(c => c.category === 'database').length },
    { value: 'design', label: 'Design & UX', icon: 'Paintbrush', count: certifications.filter(c => c.category === 'design').length },
    { value: 'security', label: 'Security', icon: 'Shield', count: certifications.filter(c => c.category === 'security').length },
    { value: 'ai', label: 'AI & Machine Learning', icon: 'Brain', count: certifications.filter(c => c.category === 'ai').length }
  ];

  const filteredCertifications = selectedCategory === 'all' 
    ? certifications 
    : certifications.filter(cert => cert.category === selectedCategory);

  const getLevelColor = (level) => {
    switch (level) {
      case 'Foundation': return '#ffaa00';
      case 'Associate': return '#00d4ff';
      case 'Advanced': return '#00ff88';
      case 'Professional': return '#8844ff';
      case 'Expert': return '#ff6b35';
      default: return '#cccccc';
    }
  };

  const isExpiringSoon = (expiry) => {
    const expiryDate = new Date(expiry);
    const now = new Date();
    const sixMonthsFromNow = new Date(now.getTime() + (6 * 30 * 24 * 60 * 60 * 1000));
    return expiryDate <= sixMonthsFromNow;
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center mb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="inline-flex items-center space-x-3 mb-4"
        >
          <div className="w-12 h-12 bg-gradient-to-br from-brand-cyan to-brand-purple rounded-lg flex items-center justify-center cyber-glow">
            <Icon name="Award" size={24} color="#ffffff" />
          </div>
          <h2 className="font-orbitron font-bold text-3xl text-foreground">
            Certification Arsenal
          </h2>
        </motion.div>
        <p className="text-text-secondary max-w-2xl mx-auto">
          Professional certifications and achievements that validate expertise across multiple technology.
        </p>
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap justify-center gap-2 mb-8">
        {categories.map((category) => (
          <Button
            key={category.value}
            variant={selectedCategory === category.value ? "default" : "outline"}
            size="sm"
            iconName={category.icon}
            iconPosition="left"
            iconSize={16}
            onClick={() => setSelectedCategory(category.value)}
            className={`${selectedCategory === category.value ? 'cyber-glow' : ''} transition-all duration-300`}
          >
            {category.label} ({category.count})
          </Button>
        ))}
      </div>

      {/* Certifications Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredCertifications.map((cert, index) => (
          <motion.div
            key={cert.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className={`glass-morphism p-6 rounded-2xl cyber-border cursor-pointer transition-all duration-300 ${
              hoveredBadge === cert.id ? 'cyber-border-active cyber-glow-intense' : 'hover:cyber-glow'
            }`}
            onMouseEnter={() => setHoveredBadge(cert.id)}
            onMouseLeave={() => setHoveredBadge(null)}
          >
            {/* Badge Header */}
            <div className="flex items-start justify-between mb-4">
              <div 
                className="w-16 h-16 rounded-xl flex items-center justify-center cyber-glow"
                style={{ background: `linear-gradient(135deg, ${cert.color}80, ${cert.color}40)` }}
              >
                <Icon name={cert.icon} size={28} color="#ffffff" />
              </div>
              <div className="flex flex-col items-end space-y-2">
                {cert.verified && (
                  <div className="flex items-center space-x-1 bg-brand-green/20 px-2 py-1 rounded-full">
                    <Icon name="CheckCircle" size={12} color="#00ff88" />
                    <span className="text-xs text-brand-green font-mono">VERIFIED</span>
                  </div>
                )}
                <div 
                  className="px-2 py-1 rounded-full text-xs font-mono"
                  style={{ 
                    backgroundColor: `${getLevelColor(cert.level)}20`,
                    color: getLevelColor(cert.level)
                  }}
                >
                  {cert.level.toUpperCase()}
                </div>
              </div>
            </div>

            {/* Certification Info */}
            <div className="mb-4">
              <h3 className="font-exo font-bold text-lg text-foreground mb-1">
                {cert.name}
              </h3>
              <p className="text-sm text-text-secondary mb-2">{cert.issuer}</p>
              <p className="text-xs text-text-tertiary">{cert.description}</p>
            </div>

            {/* Skills */}
            <div className="mb-4">
              <div className="flex flex-wrap gap-1">
                {cert.skills.slice(0, 3).map((skill) => (
                  <span
                    key={skill}
                    className="px-2 py-1 bg-surface/50 rounded text-xs text-foreground"
                  >
                    {skill}
                  </span>
                ))}
                {cert.skills.length > 3 && (
                  <span className="px-2 py-1 bg-surface/50 rounded text-xs text-text-secondary">
                    +{cert.skills.length - 3} more
                  </span>
                )}
              </div>
            </div>

            {/* Dates */}
            <div className="flex justify-between items-center text-xs mb-4">
              <div>
                <span className="text-text-tertiary">Issued: </span>
                <span className="text-brand-cyan font-mono">
                  {new Date(cert.date).toLocaleDateString()}
                </span>
              </div>
              <div>
                <span className="text-text-tertiary">Expires: </span>
                <span 
                  className={`font-mono ${
                    isExpiringSoon(cert.expiry) ? 'text-brand-orange' : 'text-brand-green'
                  }`}
                >
                  {new Date(cert.expiry).toLocaleDateString()}
                </span>
              </div>
            </div>

            {/* Expiry Warning */}
            {isExpiringSoon(cert.expiry) && (
              <div className="flex items-center space-x-2 p-2 bg-brand-orange/20 rounded-lg mb-4">
                <Icon name="AlertTriangle" size={14} color="#ffaa00" />
                <span className="text-xs text-brand-orange">Renewal required soon</span>
              </div>
            )}

            {/* Actions */}
            <div className="flex space-x-2">
              <Button
                variant="outline"
                size="xs"
                fullWidth
                iconName="ExternalLink"
                iconPosition="left"
                iconSize={12}
                onClick={() => window.open(cert.verificationUrl, '_blank')}
              >
                Verify
              </Button>
              <Button
                variant="ghost"
                size="xs"
                iconName="Download"
                iconSize={12}
                onClick={() => {
                  // Simulate certificate download
                  const link = document.createElement('a');
                  link.href = '#';
                  link.download = `${cert.name.replace(/\s+/g, '_')}_Certificate.pdf`;
                  link.click();
                }}
              />
            </div>

            {/* Expanded Details */}
            {hoveredBadge === cert.id && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="mt-4 pt-4 border-t border-border"
              >
                <h4 className="font-exo font-semibold text-foreground mb-2">All Skills</h4>
                <div className="flex flex-wrap gap-1">
                  {cert.skills.map((skill) => (
                    <span
                      key={skill}
                      className="px-2 py-1 bg-surface/30 rounded text-xs text-text-secondary"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </motion.div>
            )}
          </motion.div>
        ))}
      </div>

      {/* Stats Summary */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="glass-morphism p-8 rounded-2xl cyber-border"
      >
        <h3 className="font-orbitron font-bold text-xl text-foreground mb-6 text-center">
          Certification Statistics
        </h3>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="text-3xl font-orbitron font-bold text-brand-cyan mb-2">
              {certifications.length}
            </div>
            <div className="text-sm text-text-secondary">Total Certifications</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-orbitron font-bold text-brand-green mb-2">
              {certifications.filter(c => c.verified).length}
            </div>
            <div className="text-sm text-text-secondary">Verified</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-orbitron font-bold text-brand-purple mb-2">
              {certifications.filter(c => c.level === 'Professional' || c.level === 'Expert').length}
            </div>
            <div className="text-sm text-text-secondary">Professional+</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-orbitron font-bold text-brand-orange mb-2">
              {certifications.filter(c => isExpiringSoon(c.expiry)).length}
            </div>
            <div className="text-sm text-text-secondary">Renewal Due</div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default CertificationBadges;